import { apiClient } from './client';
import type { ApiResponse, PagedResult } from '@/types/api.types';
import type {
  ApPaymentDto,
  ApPaymentListDto,
  ApPaymentFilterDto,
  CreateApPaymentRequest,
  UpdateApPaymentRequest,
} from '@/types/apPayment.types';

export const apPaymentApi = {
  /** GET /api/v1/ap-payments — paginated list */
  getList: (filter?: ApPaymentFilterDto) =>
    apiClient.get<ApiResponse<PagedResult<ApPaymentListDto>>>('/ap-payments', { params: filter }),

  /** GET /api/v1/ap-payments/{id} — full payment with lines */
  getById: (id: string) =>
    apiClient.get<ApiResponse<ApPaymentDto>>(`/ap-payments/${id}`),

  /** POST /api/v1/ap-payments — create new payment (Draft) */
  /**
   * GET /api/v1/ap-payments/default-payee — the cheque payee this supplier defaults to (GAP-004).
   *
   * Asked of the server rather than assembled here from the supplier and account records, so what
   * the operator sees is exactly what posting writes into the cheque register.
   */
  defaultPayee: (supplierId: string) =>
    apiClient.get<ApiResponse<{ payeeName: string; source: string; accountCode?: string | null; accountName?: string | null }>>(
      '/ap-payments/default-payee', { params: { supplierId } }),

  /**
   * GET /api/v1/ap-payments/default-crossing — how a cheque on this line will be crossed if
   * nobody states otherwise (ML-09).
   *
   * Asked of the server for the same reason as defaultPayee: the precedence is account, then bank
   * account, and a second copy of it in the screen would eventually disagree with the register.
   */
  defaultCrossing: (supplierId: string, bankAccountId?: string | null) =>
    apiClient.get<ApiResponse<{ crossingType: string | null; source: string | null }>>(
      '/ap-payments/default-crossing',
      { params: { supplierId, ...(bankAccountId ? { bankAccountId } : {}) } }),

  create: (data: CreateApPaymentRequest) =>
    apiClient.post<ApiResponse<{ id: string }>>('/ap-payments', data),

  /** PUT /api/v1/ap-payments/{id} — update draft payment */
  update: (id: string, data: UpdateApPaymentRequest) =>
    apiClient.put<ApiResponse<object>>(`/ap-payments/${id}`, data),

  /** POST /api/v1/ap-payments/{id}/submit — submit for approval */
  submit: (id: string) =>
    apiClient.post<ApiResponse<object>>(`/ap-payments/${id}/submit`),

  /** POST /api/v1/ap-payments/{id}/approve — approve the payment */
  approve: (id: string, remarks?: string) =>
    apiClient.post<ApiResponse<object>>(`/ap-payments/${id}/approve`, { remarks }),

  /** POST /api/v1/ap-payments/{id}/reject — reject with reason */
  reject: (id: string, reason: string) =>
    apiClient.post<ApiResponse<object>>(`/ap-payments/${id}/reject`, { reason }),

  /** POST /api/v1/ap-payments/{id}/post — post to general ledger */
  post: (id: string) =>
    apiClient.post<ApiResponse<object>>(`/ap-payments/${id}/post`),

  /** POST /api/v1/ap-payments/{id}/reverse — create reversal payment */
  reverse: (id: string, reversalDate: string, reversalPostingDate: string, narration?: string) =>
    apiClient.post<ApiResponse<{ id: string }>>(`/ap-payments/${id}/reverse`, {
      reversalDate,
      reversalPostingDate,
      narration,
    }),

  /** POST /api/v1/ap-payments/{id}/cancel — cancel the payment */
  cancel: (id: string, reason?: string) =>
    apiClient.post<ApiResponse<object>>(`/ap-payments/${id}/cancel`, { reason }),

  /** DELETE /api/v1/ap-payments/{id} — soft-delete (draft only) */
  delete: (id: string) =>
    apiClient.delete<ApiResponse<object>>(`/ap-payments/${id}`),
};
